// ミッション検索結果から統計グラフを表示する
import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import dynamic from 'next/dynamic';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import type { GroupBy, Unit } from './LaunchStats';
import { MissionReadResponse } from '@/lib/models/mission';

// SSR環境ではrechartsがwindowを参照してエラーになるため動的インポートする
const LaunchStats = dynamic(
  () => import('./LaunchStats').then((mod) => mod.LaunchStats),
  {
    ssr: false,
    loading: () => null,
  },
);

export const MissionStats = ({
  items,
  defaultGroupBy = 'rocket',
  unit = 'month',
  // 表示するグループ数の上限（その他を含めない）
  legendLimit = 5,
}: {
  items: MissionReadResponse[];
  defaultGroupBy?: GroupBy;
  unit?: Unit;
  legendLimit?: number;
}) => {
  const [groupBy, setGroupBy] = useState<GroupBy>(defaultGroupBy);
  // defaultGroupBy が変化したら state を更新する
  useEffect(() => {
    setGroupBy(defaultGroupBy);
  }, [defaultGroupBy]);
  const [periodUnit, setPeriodUnit] = useState<Unit>(unit);
  const [confirmedDateOnly, setConfirmedDateOnly] = useState(true);
  const [confirmedTimeOnly, setConfirmedTimeOnly] = useState(true);
  const [launchOnly, setLaunchOnly] = useState(true);

  // データが変化したら時間範囲に応じて単位を切り替える
  useEffect(() => {
    if (items.length === 0) return;
    const times = items
      .map((i) => new Date(i.datetime).getTime())
      .filter((t) => !Number.isNaN(t));
    const min = Math.min(...times);
    const max = Math.max(...times);
    const diffDays = (max - min) / (1000 * 60 * 60 * 24);
    if (diffDays >= 3650) {
      setPeriodUnit('decade');
    } else if (diffDays >= 365) {
      setPeriodUnit('year');
    } else if (diffDays >= 31) {
      setPeriodUnit('month');
    } else if (diffDays >= 7) {
      setPeriodUnit('week');
    } else {
      setPeriodUnit('day');
    }
  }, [items]);
  const chartItems = items;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography
        id="stats-modal-title"
        variant="h6"
        component="h2"
        sx={{ mb: 1 }}
      >
        打ち上げ統計
      </Typography>
      <FormControl size="small" sx={{ mb: 2, mr: 2 }}>
        <InputLabel id="group-by-label">グループ化</InputLabel>
        <Select
          labelId="group-by-label"
          label="グループ化"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as GroupBy)}
        >
          <MenuItem value="rocket">ロケット</MenuItem>
          <MenuItem value="provider">プロバイダー</MenuItem>
          <MenuItem value="site">発射場</MenuItem>
          <MenuItem value="result">打ち上げ結果</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ mb: 2, mr: 2 }}>
        <InputLabel id="unit-label">単位</InputLabel>
        <Select
          labelId="unit-label"
          label="単位"
          value={periodUnit}
          onChange={(e) => setPeriodUnit(e.target.value as Unit)}
        >
          <MenuItem value="day">日次</MenuItem>
          <MenuItem value="week">週次</MenuItem>
          <MenuItem value="month">月次</MenuItem>
          <MenuItem value="year">年次</MenuItem>
          <MenuItem value="decade">10年次</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmedDateOnly}
            onChange={(e) => setConfirmedDateOnly(e.target.checked)}
            size="small"
          />
        }
        label="日付確定のみ"
        sx={{ mr: 2 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmedTimeOnly}
            onChange={(e) => setConfirmedTimeOnly(e.target.checked)}
            size="small"
          />
        }
        label="時刻確定のみ"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={launchOnly}
            onChange={(e) => setLaunchOnly(e.target.checked)}
            size="small"
          />
        }
        label="打ち上げのみ"
      />
      <LaunchStats
        items={chartItems}
        groupBy={groupBy}
        unit={periodUnit}
        legendLimit={legendLimit}
        requireConfirmedDate={confirmedDateOnly}
        requireConfirmedTime={confirmedTimeOnly}
        launchOnly={launchOnly}
      />
    </Box>
  );
};

export default MissionStats;
